import { buildFaqJsonLd, type FaqEntry } from "@/lib/seoAnswer";
import { fontStack } from "@/theme";
import { Box, Typography } from "@mui/material";
import { useEffect } from "react";

const FAQ_SCRIPT_ID = "faq-structured-data";

export default function FaqSection({ items }: { items: FaqEntry[] }) {
  const serialized = JSON.stringify(buildFaqJsonLd(items));

  useEffect(() => {
    const existing = document.getElementById(FAQ_SCRIPT_ID);
    const script = existing ?? document.createElement("script");
    script.id = FAQ_SCRIPT_ID;
    script.setAttribute("type", "application/ld+json");
    script.textContent = serialized;
    if (!existing) document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, [serialized]);

  return (
    <Box
      component="section"
      aria-labelledby="faq-heading"
      sx={{ mt: 2.5, maxWidth: 780 }}
    >
      <Typography
        id="faq-heading"
        component="h2"
        sx={{
          fontFamily: fontStack.serif,
          fontSize: { xs: "1.25rem", md: "1.5rem" },
          letterSpacing: 0,
          color: "ink.main",
          mb: 1.25,
        }}
      >
        Common questions
      </Typography>
      {items.map((item) => (
        <Box key={item.question} component="article" sx={{ mb: 1.75 }}>
          <Typography
            component="h3"
            sx={{
              fontSize: "1rem",
              fontWeight: 600,
              color: "ink.main",
              mb: 0.5,
            }}
          >
            {item.question}
          </Typography>
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "0.95rem",
              lineHeight: 1.7,
            }}
          >
            {item.answer}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
